import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AjaxService } from '../../../service/ajax.service';
import { UtilService } from '../../../service/util.service';
import { API } from '../../../../environments/environment';
import { UserService } from 'src/app/services/user.service';

interface Cliente {
  id?: number;
  nome?: string;
  sobrenome?: string;
  email?: string;
  cpf?: string;
  carteira?: string;
  // Campos do backend em lowercase
  id_paciente_clinica?: number;
  id_empresa?: number;
  id_usuario?: number;
  data_cadastro?: Date;
  data_nascimento?: Date;
  id_cidade?: number;
  rua?: string;
  bairro?: string;
  complemento?: string;
  cep?: string;
  numero?: string;
  telefone_residencial?: string;
  telefone_celular?: string;
  rg?: string;
  dante_causa?: string;
  genero?: number;
  telefone_adicional?: string;
  observacoes?: string;
  nome_mae?: string;
  nome_pai?: string;
  arvore?: string;
  link_pasta?: string;
  sharepoint?: string;
  pasta_fisica?: boolean;
  senha?: string;
  senha_confirmacao?: string;
  ativo?: boolean;
  pais?: string;
  estado?: string;
  cidade?: string;
}

interface Filtros {
  carteira: string;
  nome: string;
  email: string;
}

@Component({
  selector: 'app-consultar-cliente',
  templateUrl: './consultar-cliente.component.html',
  styleUrls: ['./consultar-cliente.component.scss']
})
export class ConsultarClienteComponent implements OnInit {

  // Dados dos clientes
  clientes: Cliente[] = [];
  clientesFiltrados: Cliente[] = [];

  // Filtros
  filtros: Filtros = {
    carteira: '',
    nome: '',
    email: ''
  };

  // Pesquisa na tabela
  termoPesquisa: string = '';

  // Paginação
  paginaAtual: number = 1;
  itensPorPagina: number = 10;
  totalItens: number = 0;
  totalPaginas: number = 0;
  clientesPaginados: Cliente[] = [];

  constructor(
    private router: Router,
    private ajaxService: AjaxService,
    private util: UtilService,
    private auth: UserService
  ) { }

  ngOnInit(): void {
    this.carregarClientes();
  }

  // Carregar dados dos clientes via API
  carregarClientes(): void {
    let url = API + "empresa/Paciente/ConsultarClienteUsuario";
   
    this.util.showLoading();
    
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.util.closeLoading();
        // Processar a resposta da API
        if (response && response.data && Array.isArray(response.data)) {
          // Filtrar apenas clientes válidos e converter para lowercase
          this.clientes = response.data
            .filter(cliente => cliente && typeof cliente === 'object')
            .map(cliente => this.util.converterParaLowercase(cliente));

            console.log(this.clientes);
        } else {
          this.clientes = [];
        }
        this.clientesFiltrados = [...this.clientes];
        this.aplicarFiltros();
      },
      error: (error) => {
        this.util.closeLoading();
        console.error('Erro ao carregar clientes:', error);
        this.util.toasterShowError('Erro', 'Erro ao carregar lista de clientes');
        
        this.clientes = [];
        this.clientesFiltrados = [];
        this.aplicarFiltros();
      }
    });
  }


  // Aplicar filtros
  aplicarFiltros(): void {
    this.clientesFiltrados = this.clientes.filter(cliente => {
      // Verificar se o cliente existe e tem as propriedades necessárias
      if (!cliente) return false;
      
      const matchCarteira = !this.filtros.carteira || 
        (cliente.carteira && cliente.carteira.toLowerCase().includes(this.filtros.carteira.toLowerCase()));
      
      const matchNome = !this.filtros.nome || 
        (cliente.nome && cliente.nome.toLowerCase().includes(this.filtros.nome.toLowerCase())) ||
        (cliente.sobrenome && cliente.sobrenome.toLowerCase().includes(this.filtros.nome.toLowerCase()));
      
      const matchEmail = !this.filtros.email || 
        (cliente.email && cliente.email.toLowerCase().includes(this.filtros.email.toLowerCase()));
      
      return matchCarteira && matchNome && matchEmail;
    });
    
    // Aplicar paginação
    this.aplicarPaginação();
  }

  // Aplicar paginação
  aplicarPaginação(): void {
    this.totalItens = this.clientesFiltrados.length;
    this.totalPaginas = Math.ceil(this.totalItens / this.itensPorPagina);
    
    // Garantir que a página atual não exceda o total de páginas
    if (this.paginaAtual > this.totalPaginas && this.totalPaginas > 0) {
      this.paginaAtual = this.totalPaginas;
    }
    
    // Calcular índices para a página atual
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    
    // Obter os itens da página atual
    this.clientesPaginados = this.clientesFiltrados.slice(inicio, fim);
  }

  // Pesquisar clientes
  pesquisarClientes(): void {
    this.paginaAtual = 1; // Resetar para primeira página ao pesquisar
    this.aplicarFiltros();
  }

  // Limpar filtros
  limparFiltros(): void {
    this.filtros = {
      carteira: '',
      nome: '',
      email: ''
    };
    this.termoPesquisa = '';
    this.paginaAtual = 1; // Resetar para primeira página
    this.clientesFiltrados = [...this.clientes];
    this.aplicarPaginação();
  }

  // Pesquisar na tabela
  pesquisarTabela(): void {
    this.paginaAtual = 1; // Resetar para primeira página ao pesquisar
    
    if (!this.termoPesquisa.trim()) {
      this.aplicarFiltros();
      return;
    }

    const termo = this.termoPesquisa.toLowerCase();
    this.clientesFiltrados = this.clientes.filter(cliente => {
      // Verificar se o cliente existe e tem as propriedades necessárias
      if (!cliente) return false;
      
      return (cliente.nome && cliente.nome.toLowerCase().includes(termo)) ||
             (cliente.sobrenome && cliente.sobrenome.toLowerCase().includes(termo)) ||
             (cliente.email && cliente.email.toLowerCase().includes(termo)) ||
             (cliente.cpf && cliente.cpf.includes(termo)) ||
             (cliente.carteira && cliente.carteira.toLowerCase().includes(termo));
    });
    
    this.aplicarPaginação();
  }

  // Métodos de navegação da paginação
  irParaPagina(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPaginas) {
      this.paginaAtual = pagina;
      this.aplicarPaginação();
    }
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.aplicarPaginação();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.aplicarPaginação();
    }
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1; // Resetar para primeira página
    this.aplicarPaginação();
  }

  // Gerar array de números de páginas para exibição
  getNumerosPaginas(): number[] {
    const numeros: number[] = [];
    const inicio = Math.max(1, this.paginaAtual - 2);
    const fim = Math.min(this.totalPaginas, this.paginaAtual + 2);
    
    for (let i = inicio; i <= fim; i++) {
      numeros.push(i);
    }
    
    return numeros;
  }

  // Método para calcular o fim dos itens exibidos
  getFimItensExibidos(): number {
    return Math.min(this.paginaAtual * this.itensPorPagina, this.totalItens);
  }

  // Ver detalhes do cliente
  verDetalhes(cliente: Cliente): void {
    console.log('Ver detalhes do cliente:', cliente);
    this.clienteDetalhes = cliente;
    this.modalVisualizacaoVisible = true;
  }

  // Editar cliente
  editarCliente(cliente: Cliente): void {
    console.log('Editar cliente:', cliente);
    // Navegar para a tela de cadastro com os dados do cliente
    this.router.navigate(['/cadastro-cliente'], { 
      queryParams: { 
        modo: 'edicao',
        clienteId: cliente.id,
        clienteData: JSON.stringify(cliente)
      } 
    });
  }

  // Excluir cliente
  excluirCliente(cliente: Cliente): void {
    console.log('Excluir cliente:', cliente);
    if (confirm(`Tem certeza que deseja excluir o cliente ${cliente.nome} ${cliente.sobrenome}?`)) {
      this.clientes = this.clientes.filter(c => c.id !== cliente.id);
      this.aplicarFiltros();
      alert('Cliente excluído com sucesso!');
    }
  }

  // Modal de cadastro
  modalCadastroVisible: boolean = false;
  clienteSelecionado: any = null;

  // Modal de visualização
  modalVisualizacaoVisible: boolean = false;
  clienteDetalhes: any = null;

  // Novo cliente
  novoCliente(): void {
    this.clienteSelecionado = null;
    this.modalCadastroVisible = true;
  }

  // Fechar modal de cadastro
  fecharModalCadastro(fechado: boolean): void {
    this.modalCadastroVisible = false;
    this.clienteSelecionado = null;
  }

  // Cliente salvo
  onClienteSalvo(cliente: any): void {
    console.log('Cliente salvo:', cliente);
    // Recarregar lista de clientes
    this.carregarClientes();
  }

  // Fechar modal de visualização
  fecharModalVisualizacao(fechado: boolean): void {
    this.modalVisualizacaoVisible = false;
    this.clienteDetalhes = null;
  }

} 